import Leaflet from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {MapContainer, Marker, TileLayer} from 'react-leaflet';
import {usePageText} from '../context/page-text-context';
import {useRepair} from '../context/repair-context';
import {objectToQueryString} from '../utils/objects';
import {snakeCaseKeys} from '../utils/snake-case-keys';
import {Button} from './button';
import {Loading} from './loading';
import OptionButton from './options-button';
import RouteContainer from './route-container';

const tileUrl = `https://mt.google.com/vt/lyrs=m&x={x}&y={y}&z={z}`;
const techMarker = '/tracker-marker-tech.png';
const shadowMarker = '/tracker-marker-shadow.png';
const techIcon = Leaflet.icon({
  iconUrl: techMarker,
  iconAnchor: [12, 40],
  iconSize: [24, 40],
  popupAnchor: [0, 0],
  shadowUrl: shadowMarker,
});

const userMarker = '/tracker-marker-user.png';
const userIcon = Leaflet.icon({
  iconUrl: userMarker,
  iconAnchor: [12, 40],
  iconSize: [24, 40],
  popupAnchor: [0, 0],
  shadowUrl: shadowMarker,
});

const alternateMarker = '/tracker-marker-customer.png';
const alternateIcon = Leaflet.icon({
  iconUrl: alternateMarker,
  iconAnchor: [12, 40],
  iconSize: [24, 40],
  popupAnchor: [0, 0],
  shadowUrl: shadowMarker,
});

function StoreList({handleModalClose = () => {}, setSelectingStore = () => {}}) {
  const {inStoreLocation, confirmButton, cancelButton} = usePageText();
  const {
    repair: {
      latitude,
      longitude,
      id: repairId,
      selectedStore: {storeId: originalStoreId},
      issues,
      deviceColor = 'unknown',
    },
    group: {id: groupId},
    refetch,
  } = useRepair();
  const [nearbyStores, setNearbyStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showStores, setShowStores] = useState(4);
  const [center, setCenter] = useState({
    latitude: latitude,
    longitude: longitude,
  });
  const storesRemaining = nearbyStores.length - showStores;
  const baseUrl = import.meta.env.VITE_REACT_APP_API_HOST;

  const handleNewStoreSubmission = async () => {
    const requestUrl = `${baseUrl}/web_repairs/${repairId}/update_selected_store`;
    const options = {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({store: {id: selectedStore.id}}),
    };

    setLoading(true);
    try {
      const response = await fetch(requestUrl, options);
      if (response.status === 201) {
        setLoading(false);
        refetch();
      }
    } catch (error) {
      setLoading(false);
      setError(error);
      console.warn(error);
    }
  };

  useEffect(() => {
    const query = {
      latitude,
      longitude,
      groupId,
      repairId,
      color: deviceColor,
      issueName: issues[0],
      carrier: '',
      capacity: '',
      imei: '',
      phoneNumber: '',
    };
    const requestUrl = `${baseUrl}/nearby_stores?${objectToQueryString(snakeCaseKeys(query))}`;
    const options = {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    const asyncFetchNearbyStores = async () => {
      setLoading(true);
      try {
        const response = await fetch(requestUrl, options);
        const data = await response.json();
        setNearbyStores(data.sort((a, b) => a.miles_from_location - b.miles_from_location));
        setLoading(false);
      } catch (error) {
        console.warn(error);
      }
    };

    asyncFetchNearbyStores();
  }, [latitude, longitude, groupId, repairId, deviceColor, issues, baseUrl]);

  useEffect(() => {
    const storeInNearbyStores = nearbyStores?.find((store) => store.id === originalStoreId);

    if (storeInNearbyStores) {
      setSelectedStore(storeInNearbyStores);
    }
  }, [nearbyStores, originalStoreId]);

  const selectOption = (value) => {
    setSelectedStore(value);
    setCenter({latitude: value.latitude, longitude: value.longitude});
  };
  const bounds = nearbyStores.map((store) => [store.latitude, store.longitude]);

  const storesShowing = nearbyStores.slice(0, showStores);

  const {storeNearby, fewStoresNearby, loadingStores, distanceMeasure, away, loadMore} = inStoreLocation;

  let title = '';
  if (loading) {
    title = loadingStores;
  } else if (nearbyStores.length === 1) {
    title = storeNearby;
  } else if (nearbyStores.length > 1) {
    title = fewStoresNearby;
  }

  return (
    <RouteContainer title={title}>
      {!loading && nearbyStores.length > 0 ? (
        <>
          <div className={styles.mainContainer}>
            <div className={styles.storeContainer}>
              {storesShowing.map((store) => {
                const {address_components, city = '', id, name = '', state = '', miles_from_location} = store;
                const {route = '', street_number = ''} = address_components;

                let distance = '';
                if (distanceMeasure === 'km.') {
                  distance = (miles_from_location * 1.60934).toFixed(2);
                } else {
                  distance = miles_from_location;
                }

                return (
                  <OptionButton
                    className={styles.optionButton}
                    key={`${id}-${name}`}
                    onClick={() => {
                      selectOption(store);
                    }}
                    selected={selectedStore?.id === store.id}>
                    <div className={styles.nameContainer}>
                      <p className={styles.nameContainerParagraph}>{name}</p>
                      <span className={styles.milesAway}>
                        {distance} {distanceMeasure} {away}
                      </span>
                    </div>
                    <div className={styles.subtitleContainer}>
                      <span className={styles.optionSubtitle}>{`${street_number} ${route}`}</span>
                      <span className={styles.optionSubtitle}>{`${city}, ${state}`}</span>
                    </div>
                  </OptionButton>
                );
              })}
              {storesRemaining > 0 && (
                <Button className={styles.loadMore} onClick={() => setShowStores(showStores + storesRemaining)}>
                  {loadMore}
                </Button>
              )}
            </div>

            <div className={styles.mapContainer}>
              <StoreMap
                bounds={bounds}
                center={center}
                currentLocation={{longitude, latitude}}
                handleMarkerClick={selectOption}
                selectedStore={selectedStore}
                stores={storesShowing}
              />
            </div>
          </div>

          {error ? <div className={styles.error}>{error.message}</div> : null}
          <div className={styles.modalButtonGroup}>
            <div className={styles.buttonsWrapper}>
              <Button
                className={styles.cancel}
                name="cancel"
                onClick={() => {
                  handleModalClose();
                  setSelectingStore(false);
                }}>
                {cancelButton}
              </Button>
              <Button
                className={styles.confirm}
                disabled={!selectedStore}
                name="confirm"
                onClick={handleNewStoreSubmission}>
                {confirmButton}
              </Button>
            </div>
          </div>
        </>
      ) : (
        <Loading />
      )}
    </RouteContainer>
  );
}

export const styles = {
  title: 'text-[2.6rem]',
  mainContainer: 'flex justify-between h-[50vh]',
  nameContainer: 'w-full font-light flex justify-between pr-[15px]',
  nameContainerParagraph: 'text-[#00aeef] m-0 text-[0.95rem] font-bold leading-[1.2] break-words',
  milesAway: 'block float-right text-[0.9rem] pl-[20px]',
  storeContainer: 'flex-1 overflow-y-scroll max-h-[50vh] w-[17vw] mr-[10px]',
  mapContainer: 'flex flex-1',
  optionButton: 'mb-[10px] items-start text-left p-[10px] break-words',
  subtitleContainer: 'flex items-start flex-col font-medium',
  error: 'text-red-600 font-bold',
  modalButtonGroup: 'flex justify-end pt-[10px]',
  buttonsWrapper: 'flex gap-[25px] w-1/2',
  cancel: 'bg-none border border-[#00aeef]',
  cancelButtonText: 'text-[#00aeef]',
  confirm: 'text-white border border-[#00aeef]',
  buttonText: 'font-bold',
  loadMore: 'bg-none text-[#00aeef] border border-[#00aeef] font-bold',
  optionSubtitle: 'text-xs p-0',
};

function StoreMap({_bounds, center, currentLocation, handleMarkerClick, selectedStore, stores}) {
  const mapRef = useRef(null);
  const [zoom, setZoom] = useState(12);
  const [unmountMap, setunmountMap] = useState(false);
  useLayoutEffect(() => {
    setunmountMap(false);
    return () => {
      setunmountMap(true);
    };
  }, []);

  useEffect(() => {
    if (mapRef.current) {
      const mapBounds = mapRef.current?.leafletElement?.getBounds();
      const isMarkerInViewport = (latLng) => mapBounds?.contains(latLng);

      const storesInViewportArray = stores.map((store) => {
        return isMarkerInViewport([store.latitude, store.longitude]);
      });

      if (storesInViewportArray.includes(false)) {
        setTimeout(() => {
          setZoom(zoom - 2);
        }, 350);
      }
    }
  }, [mapRef, stores, zoom]);

  return !unmountMap ? (
    <MapContainer
      attributionControl={false}
      center={[center.latitude || '0', center.longitude || '0']}
      doubleClickZoom={false}
      ref={mapRef}
      scrollWheelZoom={false}
      style={{width: '400px'}}
      zoom={zoom}>
      <TileLayer url={tileUrl} />
      <Marker icon={alternateIcon} key={'user'} position={[currentLocation.latitude, currentLocation.longitude]} />
      {stores.map((store) => (
        <Marker
          icon={selectedStore && store.id === selectedStore.id ? userIcon : techIcon}
          key={store.id}
          onClick={() => handleMarkerClick(store)}
          position={[store.latitude, store.longitude]}
        />
      ))}
    </MapContainer>
  ) : null;
}

export default StoreList;
